import { createInstance, dateReviver } from '@/api';
import {
    CreateDeliverySpecificationDto,
    DeliverySpecificationClient,
    DeliverySpecificationDetailDto,
    PartialUpdateDeliverySpecificationDto,
    UpdateDeliverySpecificationDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useDeliveryEntityStore } from '../delivery-entity/delivery-entity.store';
import { useDeliveryProcessDefinitionEditStore } from '../delivery-process-definition/delivery-process-definition-edit.store';

export interface DeliverySpecificationStoreState {
    items?: { key: string; value: DeliverySpecificationDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new DeliverySpecificationClient('', createInstance(), dateReviver);

export const useDeliverySpecificationStore = defineStore('configuration-delivery-specification-store', {
    state: (): DeliverySpecificationStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): DeliverySpecificationDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        async get(id: number) {
            this.loading = true;

            try {
                const response = await client.getById(id);

                this.items = this.items.map((x) => {
                    if (x.value.some((y) => y.id === id)) {
                        x.value = [...x.value.filter((x) => x.id !== id), response];
                    } else {
                        return x;
                    }
                });
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('deliveryspecification.detail.get.error.title'),
                    error,
                    false
                );
            } finally {
                this.loading = false;
            }
        },
        async getByDefinition(id: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `definition-${id}`;

            try {
                const response = await client.getByDefinition(id);

                if (response.length) {
                    this.items = [{ key, value: response }];
                } else {
                    this.items = [];
                }
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('deliveryspecification.detail.get.error.title'),
                    error,
                    false
                );
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(definitionId: number, dto: CreateDeliverySpecificationDto) {
            let hasError = false;
            this.reloading = true;

            try {
                const response = await client.create(dto);

                await this.getByDefinition(definitionId, true);

                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('deliveryspecification.detail.error.create.title'),
                    error
                );
                return null;
            } finally {
                this.reloading = false;

                if (!hasError) {
                    useDeliveryProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(definitionId: number, id: number, dto: UpdateDeliverySpecificationDto) {
            let hasError = false;
            this.reloading = true;

            try {
                await client.update(id, dto);
                useAlertStore().setSuccess(i18n.global.t('deliveryspecification.detail.success.update.title'));
                await this.getByDefinition(definitionId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('deliveryspecification.detail.error.update.title'),
                    error
                );
            } finally {
                if (!hasError) {
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                }
                this.reloading = false;
            }
        },
        async partialUpdate(definitionId: number, id: number, dto: PartialUpdateDeliverySpecificationDto) {
            let hasError = false;
            this.reloading = true;

            try {
                await client.partialUpdate(id, dto);
                useAlertStore().setSuccess(i18n.global.t('deliveryspecification.detail.success.update.title'));
                await this.getByDefinition(definitionId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('deliveryspecification.detail.error.update.title'),
                    error
                );
            } finally {
                if (!hasError) {
                    useDeliveryProcessDefinitionEditStore().closeEdit();
                }
                this.reloading = false;
            }
        },
        async remove(definitionId: number, id: number): Promise<boolean> {
            this.reloading = true;

            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('deliveryspecification.detail.success.delete.title'));
                await this.getByDefinition(definitionId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('deliveryspecification.detail.error.delete.title'),
                    error
                );
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(id: number) {
            const key = `definition-${id}`;

            const specifications = this.items.find((x) => x.key === key)?.value;

            if (specifications) {
                specifications.forEach((specification) => {
                    useDeliveryEntityStore().clear(specification.id);
                });
            }

            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
